import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { priceFetcherV2, priceFetcherV3 } from '@/libs/fetcher/presets'
import { CoinId, CoinPrices } from '@/types/coin'

/* -------------------------------------------------------------------------- */
/*                                V3 (coin id)                                */
/* -------------------------------------------------------------------------- */

interface FetchCoinPricesByIdPayload {
  interval: string
  coinId: CoinId
  start?: number
  end?: number
}

const fetchCoinPricesById = ({ interval, coinId, ...queries }: FetchCoinPricesByIdPayload) =>
  priceFetcherV3<CoinPrices>(
    `/${interval}/${coinId}`,
    {
      queries,
    },
    {
      forceResponseType: 'json',
    },
  )

export const coinPricesByIdQueryOptions = (payload: FetchCoinPricesByIdPayload) =>
  queryOptions({
    queryKey: ['coinPricesById', payload],
    queryFn: () => fetchCoinPricesById(payload),
  })

/* -------------------------------------------------------------------------- */
/*                                  V2 (slug)                                 */
/* -------------------------------------------------------------------------- */

export interface FetchCoinPricesBySlugPayload {
  interval: 'd' | 'h' | 'w' | 'm' | 'y' | 'all' | string
  slug: string
  start?: number
  end?: number
  convert?: string
}

export const fetchCoinPricesBySlug = ({
  interval,
  slug,
  ...queries
}: FetchCoinPricesBySlugPayload) =>
  priceFetcherV2<CoinPrices>(
    `/${interval}/${slug}`,
    {
      queries: {
        ...queries,
      },
    },
    {
      forceResponseType: 'json',
    },
  )

export const coinPricesBySlugQueryOptions = (payload: FetchCoinPricesBySlugPayload) =>
  queryOptions({
    queryKey: ['coinPricesBySlug', payload],
    queryFn: () => fetchCoinPricesBySlug(payload),
    // Return empty prices object if API returns null
    select: data => data || { prices: [], price_change: 0, usd_price_change: 0 },
  })

export const useCoinPricesBySlug = (
  payload: FetchCoinPricesBySlugPayload & {
    enabled?: boolean
  },
) => useSuspenseQuery(coinPricesBySlugQueryOptions(payload))

export const useSuspenseCoinPricesBySlug = (payload: FetchCoinPricesBySlugPayload) =>
  useSuspenseQuery(coinPricesBySlugQueryOptions({ ...payload })).data
